import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Leaf, Users, Coffee, Gift, Sparkles } from 'lucide-react';

const AProposPage = () => {
  const values = [
    {
      icon: <Coffee size={32} />,
      title: 'Une rencontre, pas une vente',
      description:
        "Chez Harmony, chaque visite commence par une rencontre. Vous êtes accueilli(e) simplement, dans une ambiance détendue. On discute de vos goûts, de vos envies, parfois même de vos souvenirs.",
      color: '#5C2018',
    },
    {
      icon: <Heart size={32} />,
      title: 'Une approche sensible',
      description:
        "Contrairement aux parfumeries traditionnelles où les senteurs se croisent sans se répondre, nous proposons une approche plus calme, plus sensible. Les parfums sont racontés, partagés, vécus.",
      color: '#1E7A6A',
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Une exploration personnalisée',
      description:
        "À l'aide de touches à sentir, vous explorez une sélection pensée pour vous surprendre, vous inspirer ou vous rassurer. Aucune pression, juste le plaisir de découvrir.",
      color: '#B8922A',
    },
    {
      icon: <Leaf size={32} />,
      title: 'Engagement éco-responsable',
      description:
        "Tous nos parfums sont rechargeables. Une démarche durable qui respecte la beauté et l'environnement, pour des fragrances qui se renouvellent sans gaspillage.",
      color: '#1E7A6A',
    },
    {
      icon: <Users size={32} />,
      title: 'Un lieu de partage',
      description:
        "C'est un lieu d'échange, de curiosité et de découverte. Les parfums ne sont pas simplement exposés, ils sont vécus. Vous pouvez revenir, y repenser, ou continuer à explorer.",
      color: '#5C2018',
    },
    {
      icon: <Gift size={32} />,
      title: 'Un univers d\'exception',
      description:
        "Découvrez notre sélection minutieuse de fragrances rares et raffinées. Nos trois gammes — Iconiques, Pépites et Audacieux — vous invitent à un voyage olfactif unique, accessible à toutes les sensibilités.",
      color: '#B8922A',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16">
      {/* Hero */}
      <section className="relative py-32 px-6 bg-[#FDFAF4] border-b border-[#B8922A]/20 overflow-hidden">
        {/* Rose filigrane en arrière-plan */}
        <img
          src="/assets/rose-filigrane.jpg"
          alt=""
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] object-contain pointer-events-none select-none mix-blend-multiply opacity-[0.08]"
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <img
            src="/assets/logo-harmony.jpg"
            alt="Harmony Parfumerie"
            className="h-32 w-32 mx-auto mb-8 rounded-full object-cover shadow-[0_8px_32px_rgba(92, 32, 24,0.15)]"
          />
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Notre histoire
          </p>
          <h1
            className="text-5xl md:text-7xl font-light tracking-tight mb-6 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="apropos-title"
          >
            Le parfum, autrement.
          </h1>
          <p className="text-lg text-[#332724] font-light italic max-w-2xl mx-auto">
            « Un lieu d'échange, de curiosité et de découverte,
            <br />
            où les parfums sont racontés, partagés, vécus. »
          </p>
        </div>
      </section>

      {/* Notre parcours */}
      <section className="py-20 px-6 bg-[#FDFAF4]" data-testid="parcours-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Notre parcours
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              De Montpellier à Béziers
            </h2>
            <p className="text-lg text-[#332724] font-light max-w-2xl mx-auto leading-relaxed">
              Une aventure née à Montpellier, désormais installée durablement au cœur du
              Polygone de Béziers — et qui continue à rayonner sur la région.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Etape 1 - Montpellier ephemere */}
            <div className="bg-white p-8 rounded-2xl border border-gold relative" data-testid="parcours-etape-1">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#1E7A6A] text-[#FDFAF4] rounded-full flex items-center justify-center text-xl font-light shadow-[0_8px_24px_rgba(30,122,106,0.3)]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                1
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">
                Les débuts · Polygone Montpellier
              </p>
              <h3
                className="text-3xl mb-4 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Là où tout a commencé
              </h3>
              <p className="text-[#332724] font-light leading-relaxed">
                Tout a démarré par une série de <strong>boutiques éphémères</strong> au
                Polygone de Montpellier. Une intuition, un concept, des rencontres et
                surtout une <em>communauté grandissante</em> de passionnés du beau parfum.
                Aujourd'hui encore, nous restons proches de nos client·e·s montpelliérain·e·s
                grâce à un service de <strong>remise en mains propres</strong> sur la région.
              </p>
            </div>

            {/* Etape 2 - Beziers permanent */}
            <div className="bg-[#5C2018] text-[#FDFAF4] p-8 rounded-2xl relative" data-testid="parcours-etape-2">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#B8922A] text-[#FDFAF4] rounded-full flex items-center justify-center text-xl font-light shadow-[0_8px_24px_rgba(184,146,42,0.4)]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                2
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">
                Aujourd'hui · Polygone Béziers
              </p>
              <h3
                className="text-3xl mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Boutique permanente
              </h3>
              <p className="font-light leading-relaxed">
                Forts de cette belle aventure, nous avons franchi une nouvelle étape en
                ouvrant notre <strong>boutique permanente au Polygone de Béziers</strong>.
                Un véritable <em>bar à parfums concept store</em> à votre service du lundi
                au samedi — sans jamais perdre le fil avec nos client·e·s historiques,
                que nous continuons à servir sur Montpellier et alentours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mot de la fondatrice */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="relative">
              <img
                src="/assets/founder.jpg"
                alt="La fondatrice de Harmony Parfumerie"
                className="w-full h-auto rounded-lg shadow-[0_20px_60px_rgba(92, 32, 24,0.15)]"
                data-testid="founder-image"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#B8922A] text-[#FDFAF4] px-6 py-4 rounded-lg shadow-lg">
                <p
                  className="text-xl italic"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
                >
                  Harmony
                </p>
                <p className="text-xs uppercase tracking-[0.2em] opacity-90">La fondatrice</p>
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Le mot de la fondatrice
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Derrière Harmony,
              <br />
              une histoire vraie.
            </h2>

            <div className="space-y-5 text-[#332724] font-light leading-relaxed">
              <p>
                « Harmony, c'est avant tout une histoire de passion. Une envie simple : créer
                un lieu où le parfum ne se vend pas, il se vit.
              </p>
              <p>
                J'ai voulu offrir une expérience différente, loin des parfumeries
                traditionnelles. Ici, chaque rencontre commence par un échange, une
                discussion, parfois même un souvenir partagé.
              </p>
              <p>
                Mes fragrances accompagnent les femmes et les hommes dans leurs émotions,
                leurs réussites et leur élégance au quotidien. Parce qu'un parfum, ce n'est
                pas qu'une senteur : c'est une histoire, une mémoire, une part de vous.
              </p>
              <p
                className="text-2xl text-[#1E7A6A] mt-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Merci pour votre confiance. »
              </p>
              <p className="text-sm uppercase tracking-[0.2em] text-[#B8922A] mt-4">
                — La fondatrice de Harmony Parfumerie
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Concept */}
      <section className="py-20 px-6 bg-[#FDFAF4]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Notre concept
          </p>
          <h2
            className="text-4xl md:text-5xl font-light mb-8 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Un bar à parfums, pas une parfumerie
          </h2>
          <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
            Au cœur du Polygone de Béziers, découvrez l'univers raffiné et authentique de
            Harmony. Contrairement aux parfumeries traditionnelles où les senteurs se
            croisent sans se répondre, notre bar à parfums vous propose une approche plus
            calme, plus sensible.
          </p>
          <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
            C'est un lieu d'échange, de curiosité et de découverte, où les parfums ne sont
            pas simplement exposés. Ils sont racontés, partagés, vécus.
          </p>
          <p
            className="text-2xl text-[#1E7A6A] mt-8 italic"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            « Si une fragrance vous touche, vous pouvez repartir avec.
            <br />
            Sinon, rien ne presse : vous pouvez revenir, y repenser,
            <br />
            ou continuer à explorer. »
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4 text-center">
            Nos valeurs
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Ce qui nous anime
          </h2>
          <p className="text-center text-[#332724] font-light mb-16 max-w-2xl mx-auto italic text-lg">
            « Six engagements qui guident chacune de nos rencontres »
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="perfume-card bg-[#FDFAF4] p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
                data-testid={`value-${index}`}
              >
                <div className="mb-4" style={{ color: value.color }}>
                  {value.icon}
                </div>
                <h3
                  className="text-2xl mb-3"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: 'italic',
                    color: value.color,
                  }}
                >
                  {value.title}
                </h3>
                <p className="text-[#332724] font-light leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1E7A6A] text-[#FDFAF4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-4xl md:text-5xl font-light mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Venez nous rencontrer
          </h2>
          <p className="text-lg font-light mb-8 leading-relaxed">
            3 Carrefour de l'Hours, Niveau Écluses
            <br />
            Polygone de Béziers
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/rendez-vous"
              className="inline-block bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm transition-colors"
              data-testid="cta-rdv-apropos"
            >
              Prendre rendez-vous
            </Link>
            <Link
              to="/personnalisation"
              className="inline-block bg-transparent border border-[#FDFAF4] hover:bg-[#FDFAF4] hover:text-[#1E7A6A] px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm transition-colors"
            >
              Diagnostic olfactif
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AProposPage;
